import { getCadFileInvitedProfiles } from '@api/cadFile.js';
import ActionMenu from '@components/Common/ActionMenu.js';
import { Avatar } from '@components/Common/Avatar.js';
import Tooltip from '@components/Common/Tooltip.js';
import InvitedUsersState from '@states/InvitedUser';
import { UIDiv, UIImage, UIText } from '@ui';
import { S3_BUCKET } from '/environment.js';

export default class GridItem extends UIDiv {
  constructor({
    model,
    mode = 'default',
    onClick,
    canCreate,
    canShare,
    canDelete,
    canUpdate,
    canMove,
    canDownload,
  }) {
    super();
    this.model = model;
    this.mode = mode;
    this.canCreate = canCreate;
    this.canShare = canShare;
    this.canDelete = canDelete;
    this.canUpdate = canUpdate;
    this.canMove = canMove;
    this.canDownload = canDownload;

    this.setClass(
      'flex flex-col grow w-1/5 h-[350px] box-border shadow-lg rounded-[var(--N4)] m-2.5 min-w-[300px] max-w-[400px] border border-gray-200 hover:cursor-pointer',
    );
    this.render();
    // if (!this.model.quotationID) {
    //   this.getInvitedUser().then(updatedModel => {
    //     this.model = updatedModel;
    //     this.render();
    //   });
    // } else {
    //   this.render();
    // }
    this.onClick(() => {
      onClick();
    });
  }

  render() {
    this.clear();
    this.initThumbnail();
    this.initInfoBox();
  }

  async getInvitedUser() {
    // Check into invited users state if available
    // If not, fetch from API
    const invitedUsers = InvitedUsersState.invitedUsers.find(
      user => user.id === this.model.id,
    );
    if (invitedUsers) {
      return {
        ...this.model,
        invitedProfiles: invitedUsers.invitedProfiles,
      };
    }
    const response = await getCadFileInvitedProfiles(this.model.id);
    const profiles = Array.isArray(response.response) ? response.response : [];
    // Update the state with fetched profiles
    InvitedUsersState.setInvitedUsers([
      ...InvitedUsersState.invitedUsers,
      { id: this.model.id, invitedProfiles: profiles },
    ]);
    return {
      ...this.model,
      invitedProfiles: profiles,
    };
  }

  initThumbnail() {
    const thumbnailContainer = new UIDiv().setClass(
      'w-full h-[250px] relative border-b flex flex-wrap content-center justify-center p-[var(--N4)] thumbnail',
    );
    const skeletonDiv = new UIDiv().setClass(
      'w-full h-full bg-gray-200 animate-pulse',
    );
    thumbnailContainer.add(skeletonDiv);
    const thumbnail =
      this.model.thumbnail === null
        ? '/static/images/logos/bee_blue.svg'
        : this.model.thumbnail;
    this.thumbnail = new UIImage(thumbnail);
    this.thumbnail.setClass(
      'object-cover block mx-auto hover:opacity-80 transition-opacity duration-300 max-h-full max-w-full',
    );
    if (this.model.thumbnail === null) this.thumbnail.addClass('max-h-[75px]');
    this.thumbnail.setStyle('display', ['none']);

    this.thumbnail.dom.onload = () => {
      skeletonDiv.setStyle('display', ['none']);
      this.thumbnail.setStyle('display', ['block']);
    };

    this.thumbnail.dom.onerror = () => {
      skeletonDiv.setStyle('display', ['none']);
      this.thumbnail.setStyle('display', ['block']);
      this.thumbnail.setSrc('/static/images/logos/bee_blue.svg');
    };

    thumbnailContainer.add(this.thumbnail);
    this.add(thumbnailContainer);
  }

  initInfoBox() {
    this.infoBox = new UIDiv();
    this.infoBox.addClass(
      'p-2 flex flex-col justify-center h-[calc(100%-250px)] relative',
    );

    const title = new UIText(this.model.name);
    title.setClass('text-lg font-bold overflow-hidden text-ellipsis pr-10');
    title.setTitle(this.model.name);
    this.infoBox.add(title);

    if (this.mode === 'public') {
      // description contains HTML; insert it as raw HTML into a container
      const descriptionContainer = new UIDiv();
      descriptionContainer.setClass('text-sm text-gray-600 truncate');
      descriptionContainer.dom.innerHTML =
        this.model.description || i18next.t('pages.projects.noDescription');
      this.infoBox.add(descriptionContainer);
    }

    const uploadDate = new UIText(
      `${i18next.t('pages.projects.createdOn')}: ${new Date(this.model.createdAt).toISOString().split('T')[0]}`,
    );
    uploadDate.setClass('text-xs');
    this.infoBox.add(uploadDate);

    const users = new UIDiv();
    users.setClass('flex flex-row');

    for (let i = 0; i < 5; i++) {
      const profile = this.model?.invitedProfiles?.[i];
      if (!profile) {
        i = 5;
        continue;
      }
      const name = `${profile.firstName ?? ''} ${profile.lastName ?? ''}`;
      const url = profile.profileImageKey
        ? S3_BUCKET + profile.profileImageKey
        : null;
      const avatar = new Avatar({
        src: url,
        name: name.trim().length > 0 ? name : profile.email,
        email: profile.email,
      });
      avatar.onMouseEnter(() => {
        Tooltip.show(
          avatar,
          name.trim().length > 0 ? name : profile.email,
          'top',
        );
      });
      avatar.onMouseLeave(() => {
        Tooltip.hide();
      });
      users.add(avatar);

      if (i === 4 && this.model?.invitedProfiles?.[i + 1]) {
        users.add(new UIText('...'));
      }
    }
    this.infoBox.add(users);

    if (this.mode !== 'public') {
      // Create popover menu trigger
      const menuTrigger = new ActionMenu({
        item: this.model,
        isTask: true,
        currentFolderId: this.model.folderId,
        selectedProjectType: this.model.projectType,
        canCreate: this.canCreate,
        canShare: this.canShare,
        canDelete: this.canDelete,
        canUpdate: this.canUpdate,
        canMove: this.canMove,
        canDownload: this.canDownload,
      });
      menuTrigger.setClass('absolute top-2 right-2');
      menuTrigger.onClick(event => {
        event.preventDefault();
        event.stopPropagation(); // Prevent click from propagating to the grid item
      });
      this.infoBox.add(menuTrigger);
    }
    this.add(this.infoBox);
  }
}
